import { Building2, Lock, type LucideIcon } from 'lucide-react';
import { useCallback, useId, useRef, useState } from 'react';

import { ShareSplitButton, type ShareVisibility } from '@/_shared/components/ShareSplitButton';
import { Popover, PopoverContent } from '@/_shared/components/ui/popover';
import { cn } from '@/_shared/lib/utils';

// Public chat sharing stays absent from the popover's radio options while AI-628
// alpha keeps anonymous chat sharing disabled (rebuild tracked in AI-632). The
// Visibility type includes 'public' so the trigger renders the Globe icon if
// the backend ever returns that value, but the user cannot select it until
// AI-632 ships the publish-token + abuse controls.
export type Visibility = ShareVisibility;

interface Props {
  visibility: Visibility;
  onUpdateVisibility: (visibility: Visibility) => void;
  chatUrl: string;
  pending?: boolean;
}

const OPTIONS: { value: Exclude<Visibility, 'public'>; label: string; icon: LucideIcon; description: string }[] = [
  { value: 'private', label: 'Only me', icon: Lock, description: 'Only you can view this chat' },
  { value: 'workspace', label: 'Workspace', icon: Building2, description: 'Anyone in your workspace can view' },
];

const HELPER_TEXT: Record<Visibility, string> = {
  private: 'Share with your workspace to get a link others can use.',
  workspace: 'Anyone in your workspace can open this link.',
  public: 'Anyone with this link can view this chat. It may also be indexed by search engines.',
};

/**
 * RadioGroup is a child of PopoverContent so its state re-mounts on each
 * popover open. Combined with the "derived from props" guard below, this
 * keeps `focusedIndex` in sync with the current `visibility` prop both on
 * popover open and across in-popover visibility mutations.
 *
 * See https://react.dev/reference/react/useState#storing-information-from-previous-renders
 */
function RadioGroup({
  visibility,
  onSelect,
  pending,
}: {
  visibility: Visibility;
  onSelect: (next: Visibility) => void;
  pending: boolean;
}): React.ReactElement {
  const computeFocusedIndex = (v: Visibility): number =>
    Math.max(
      0,
      OPTIONS.findIndex((o) => o.value === v),
    );
  const [focusedIndex, setFocusedIndex] = useState(() => computeFocusedIndex(visibility));
  const [prevVisibility, setPrevVisibility] = useState(visibility);
  if (prevVisibility !== visibility) {
    setPrevVisibility(visibility);
    setFocusedIndex(computeFocusedIndex(visibility));
  }

  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const moveFocus = useCallback((next: number) => {
    setFocusedIndex(next);
    optionRefs.current[next]?.focus();
  }, []);
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      const last = OPTIONS.length - 1;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        moveFocus(index === last ? 0 : index + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        moveFocus(index === 0 ? last : index - 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        moveFocus(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        moveFocus(last);
      }
    },
    [moveFocus],
  );

  return (
    <div className="space-y-1" role="radiogroup" aria-label="Chat visibility">
      {OPTIONS.map((opt, index) => (
        <button
          key={opt.value}
          ref={(el) => {
            optionRefs.current[index] = el;
          }}
          type="button"
          role="radio"
          aria-checked={visibility === opt.value}
          // aria-disabled (not native `disabled`) keeps the row focusable so
          // arrow-key navigation can land on it and screen readers can
          // announce the state coherently. The onClick guard below handles
          // the actual no-op behavior.
          aria-disabled={pending ? 'true' : undefined}
          tabIndex={index === focusedIndex ? 0 : -1}
          onClick={() => {
            if (pending) return;
            onSelect(opt.value);
          }}
          onKeyDown={(e) => handleKeyDown(e, index)}
          className={cn(
            'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
            visibility === opt.value
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            pending && 'cursor-not-allowed opacity-60',
          )}
          data-testid={`visibility-${opt.value}`}
        >
          <opt.icon className="size-4 shrink-0" />
          <div className="min-w-0">
            <div className="font-medium">{opt.label}</div>
            <div className="text-xs opacity-70">{opt.description}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

export function ChatSharePopover({
  visibility,
  onUpdateVisibility,
  chatUrl,
  pending = false,
}: Props): React.ReactElement {
  const helperId = useId();
  const handleSelect = useCallback(
    (next: Visibility) => {
      if (next === visibility) return;
      onUpdateVisibility(next);
    },
    [visibility, onUpdateVisibility],
  );

  return (
    <Popover>
      <ShareSplitButton visibility={visibility} shareUrl={chatUrl} />
      <PopoverContent className="w-72" align="end" aria-describedby={helperId}>
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">Share this chat</p>
          <RadioGroup visibility={visibility} onSelect={handleSelect} pending={pending} />
          <p id={helperId} className="text-xs text-muted-foreground">
            {HELPER_TEXT[visibility]}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
